// MyReviews.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Dimensions,
  RefreshControl,
  SafeAreaView,
  Platform,
  Share,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { Linking } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

const API_URL = 'https://collabbayadmin.com/APIControllers/GetBrandReviews.php';
const POSITIVE_RATING_MIN = 4;

export default function MyReviews() {
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const [reviews, setReviews] = useState([]);
  const [brandName, setBrandName] = useState('');

  const [filter, setFilter] = useState('all'); // all | five | four
  const [sort, setSort] = useState('newest'); // newest | highest

  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedShareReview, setSelectedShareReview] = useState(null);

  // Session + auth for sidenav
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none');
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const deviceW = Dimensions.get('window').width;
  const isMobile = deviceW < 800;

  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const fetchReviews = async () => {
    const dd = new Date();
    const cacheBust = dd.toISOString().slice(0, 19).replace(/[-:T]/g, '');

    const response = await fetch(`${API_URL}?d=${cacheBust}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Reviews HTTP ${response.status}`);
    }

    const json = await response.json();

    if (!json || json.success === false) {
      throw new Error(json?.message || 'Could not load reviews.');
    }

    const rows = Array.isArray(json.reviews)
      ? json.reviews
      : Array.isArray(json.data)
      ? json.data
      : [];

    // Defensive frontend filter: this page is only for reusable positive review assets.
    const positiveOnly = rows.filter((r) => Number(r.Rating) >= POSITIVE_RATING_MIN);

    setReviews(positiveOnly);
    setBrandName(json.brandName || json.BrandName || userData.Username || '');
  };

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        setUserData(data || {});
        if (data?.Username) {
          setBrandName((prev) => prev || data.Username);
        }
      })
      .catch((error) => {
        console.error('Error fetching session user data:', error);
      });
  }, []);

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';

        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((error) => {
        console.error('Error fetching auth state:', error);
        setIsAuthed(false);
        setAuthMode('none');
      })
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await fetchReviews();
      } catch (error) {
        console.error(error);
        setErr('Could not load your creator reviews right now.');
        setReviews([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onRefresh = async () => {
    try {
      setRefreshing(true);
      setErr('');
      await fetchReviews();
    } catch (error) {
      console.error(error);
      setErr('Refresh failed. Try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const visibleReviews = useMemo(() => {
    let list = reviews.filter((r) => Number(r.Rating) >= POSITIVE_RATING_MIN);

    if (filter === 'five') {
      list = list.filter((r) => Number(r.Rating) === 5);
    }

    if (filter === 'four') {
      list = list.filter((r) => Number(r.Rating) === 4);
    }

    if (sort === 'highest') {
      list.sort((a, b) => Number(b.Rating || 0) - Number(a.Rating || 0));
    } else {
      list.sort((a, b) => {
        const da = new Date(a.CreatedAt || a.DateCreated || 0).getTime();
        const db = new Date(b.CreatedAt || b.DateCreated || 0).getTime();
        return db - da;
      });
    }

    return list;
  }, [reviews, filter, sort]);

  const downloadSingleReview = async (review) => {
    const title = `${brandName || review.BrandName || 'Collabbay'} Creator Review`;
    const html = buildSingleReviewHtml(review, brandName || review.BrandName || '');
    await openPrintableHtml(html, title);
  };

  const downloadAllVisibleReviews = async () => {
    const title = `${brandName || 'Collabbay'} Creator Reviews`;
    const html = buildAllReviewsHtml(visibleReviews, brandName || '');
    await openPrintableHtml(html, title);
  };

  const openShareModal = (review) => {
    setSelectedShareReview(review);
    setShareModalVisible(true);
  };

  const closeShareModal = () => {
    setShareModalVisible(false);
    setSelectedShareReview(null);
  };

  const shareSelectedReview = async () => {
    if (!selectedShareReview) return;

    const text = buildShareText(selectedShareReview, brandName);
    const title = `${brandName || selectedShareReview.BrandName || 'Collabbay'} creator review`;

    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({
          title,
          text,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
        });
      } else {
        await Share.share({
          title,
          message: text,
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  const copySelectedReviewText = async () => {
    if (!selectedShareReview) return;

    const text = buildShareText(selectedShareReview, brandName);

    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        if (typeof window !== 'undefined') {
          window.alert('Review caption copied.');
        }
      } else {
        await Share.share({
          title: 'Creator review',
          message: text,
        });
      }
    } catch (error) {
      console.error('Copy/share text failed:', error);
    }
  };

  const openSocialShare = async (channel) => {
    if (!selectedShareReview) return;

    const text = buildShareText(selectedShareReview, brandName);
    const currentUrl =
      typeof window !== 'undefined'
        ? window.location.href
        : 'https://collabbayadmin.com/MyReviews';

    let url = '';

    if (channel === 'facebook') {
      url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodeURIComponent(text)}`;
    }

    if (channel === 'linkedin') {
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
    }

    if (channel === 'x') {
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl)}`;
    }

    if (channel === 'whatsapp') {
      url = `https://wa.me/?text=${encodeURIComponent(`${text}\n${currentUrl}`)}`;
    }

    if (channel === 'instagram') {
      // Instagram does not allow prefilled web captions/files from a browser.
      // This opens Instagram so the copied caption/review asset can be uploaded manually.
      url = 'https://www.instagram.com/';
      await copySelectedReviewText();
    }

    if (!url) return;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(url);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.appShell}>
          <View style={styles.shellRow}>
            {!isMobile && (
              <SideNav
                navigation={navigation}
                userData={userData}
                isAuthed={isAuthed}
                authChecked={authChecked}
                isSubscriber={isSubscriber}
                openInSameTab={openInSameTab}
              />
            )}

            <View style={styles.mainArea}>
              {isMobile && (
                <MobileTopBar
                  navigation={navigation}
                  title="My Reviews"
                  openInSameTab={openInSameTab}
                />
              )}

              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="black" />
                <Text style={styles.loadingText}>Loading reviews…</Text>
              </View>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.appShell}>
        <View style={styles.shellRow}>
          {!isMobile && (
            <SideNav
              navigation={navigation}
              userData={userData}
              isAuthed={isAuthed}
              authChecked={authChecked}
              isSubscriber={isSubscriber}
              openInSameTab={openInSameTab}
            />
          )}

          <View style={styles.mainArea}>
            {isMobile && (
              <MobileTopBar
                navigation={navigation}
                title="My Reviews"
                openInSameTab={openInSameTab}
              />
            )}

            <ScrollView
              contentContainerStyle={[
                styles.scrollContainer,
                isMobile && styles.scrollContainerMobile,
              ]}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.contentWrapper}>
                {!isMobile && (
                  <View style={styles.pageHeaderRow}>
                    <TouchableOpacity
                      onPress={() => navigation.goBack()}
                      style={styles.backButton}
                    >
                      <Ionicons name="arrow-back" size={20} color="black" />
                      <Text style={styles.backButtonText}>Back</Text>
                    </TouchableOpacity>

                    <View style={styles.headerRight}>
                      <TouchableOpacity
                        onPress={() => Linking.openURL('https://collabbayadmin.com')}
                        style={styles.closeButton}
                      >
                        <Ionicons name="close" size={22} color="#000" />
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                <View style={styles.cleanHeader}>
                  <View style={styles.assetExplainer}>
                    <View style={styles.assetDot} />
                    <Text style={styles.assetExplainerText}>
                      Congratulations. Recycle verified creator reviews into brand assets, download them for your website,
                      socials, reports, and online branding.
                    </Text>
                  </View>

                  <Text style={styles.kicker}>Verified creator feedback</Text>
                  <Text style={styles.header}>My Reviews</Text>
                  <Text style={styles.subhead}>
                    {visibleReviews.length} reusable positive review
                    {visibleReviews.length === 1 ? '' : 's'} from creator visits.
                  </Text>

                  <View style={styles.headerActions}>
                    <TouchableOpacity
                      style={styles.smallAction}
                      onPress={onRefresh}
                      activeOpacity={0.88}
                    >
                      <Ionicons
                        name="refresh-outline"
                        size={15}
                        color="#111"
                        style={styles.smallActionIcon}
                      />
                      <Text style={styles.smallActionText}>Refresh</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.primaryAction,
                        visibleReviews.length === 0 && styles.disabledAction,
                      ]}
                      disabled={visibleReviews.length === 0}
                      onPress={downloadAllVisibleReviews}
                      activeOpacity={0.9}
                    >
                      <LinearGradient
                        colors={['#fffdf2', '#FDF05D']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.primaryActionGradient}
                      >
                        <Ionicons
                          name="download-outline"
                          size={17}
                          color="#111"
                          style={styles.primaryActionIcon}
                        />
                        <Text style={styles.primaryActionText}>Download all reviews</Text>
                      </LinearGradient>
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={styles.controlsWrap}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.chipsScroll}
                  >
                    <Chip label="All positive" active={filter === 'all'} onPress={() => setFilter('all')} />
                    <Chip label="5 star" active={filter === 'five'} onPress={() => setFilter('five')} />
                    <Chip label="4 star" active={filter === 'four'} onPress={() => setFilter('four')} />
                    <Chip label="Newest" active={sort === 'newest'} onPress={() => setSort('newest')} />
                    <Chip label="Highest" active={sort === 'highest'} onPress={() => setSort('highest')} />
                  </ScrollView>
                </View>

                {err ? (
                  <View style={styles.errorBox}>
                    <Text style={styles.errorText}>{err}</Text>
                  </View>
                ) : null}

                {visibleReviews.length === 0 ? (
                  <EmptyState
                    hasReviews={reviews.length > 0}
                    filter={filter}
                    navigation={navigation}
                  />
                ) : (
                  <View style={styles.reviewList}>
                    {visibleReviews.map((review, index) => (
                      <ReviewCard
                        key={`${review.ReviewID || review.CollabID || index}-${index}`}
                        review={review}
                        index={index}
                        isMobile={isMobile}
                        onDownload={() => downloadSingleReview(review)}
                        onShare={() => openShareModal(review)}
                      />
                    ))}
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </View>

      <ShareReviewModal
        visible={shareModalVisible}
        review={selectedShareReview}
        brandName={brandName}
        onClose={closeShareModal}
        onDownload={() => {
          if (selectedShareReview) {
            downloadSingleReview(selectedShareReview);
          }
        }}
        onShareSheet={shareSelectedReview}
        onCopyText={copySelectedReviewText}
        onOpenSocial={openSocialShare}
      />
    </SafeAreaView>
  );
}

function SideNav({
  navigation,
  userData,
  isAuthed,
  authChecked,
  isSubscriber,
  openInSameTab,
}) {
  return (
    <View style={styles.sideNav}>
      <LinearGradient
        colors={['#050509', '#050505', '#000000']}
        style={styles.sideNavInner}
      >
        {/* subtle glow blob */}
        <LinearGradient
          colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
          style={styles.sideNavGlow}
          start={{ x: 0.1, y: 0 }}
          end={{ x: 1, y: 1 }}
        />

        {/* TOP: Logo + wordmark */}
        <View style={styles.sideNavHeader}>
          <Image
            source={{
              uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
            }}
            style={styles.sideNavLogoImage}
            resizeMode="contain"
          />
          <View>
            <Text style={styles.logoWordmark}>
              <Text style={{ fontWeight: '300' }}>collab</Text>
              bay
              <Text style={styles.logoDot}>.</Text>
            </Text>
            <Text style={styles.logoDesktopTag}>DESKTOP</Text>
          </View>
        </View>

        {/* MAIN NAV SECTION */}
        <View style={styles.sideNavSection}>
          {/* Username pill */}
          <LinearGradient
            colors={['#111111', '#2b2b2b']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.usernamePill}
          >
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={styles.usernamePillText}
            >
              {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
            </Text>
          </LinearGradient>

          {/* Home → MyPanel */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('MyPanel')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="home-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Home</Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* My Offers → MyOffers */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('MyOffers')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="pricetags-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>My Offers</Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => {
              if (authChecked && !isAuthed) {
                openInSameTab('https://collabbayadmin.com/subscribe');
              } else {
                navigation.navigate('MyCalendar');
              }
            }}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="calendar-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>My Calendar</Text>
              </View>
              {authChecked && !isAuthed && (
                <Ionicons
                  name="lock-closed-outline"
                  size={16}
                  color="#FDF05D"
                  style={styles.lockIcon}
                />
              )}
            </View>
          </TouchableOpacity>

          {/* Browse Creators → BrowseCreators */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('BrowseCreators')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="people-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Browse Creators</Text>
              </View>
            </View>
          </TouchableOpacity>

          {/* Activity → MyActivityFeed (LOCKED if NOT authed) */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => {
              if (authChecked && !isAuthed) {
                openInSameTab('https://collabbayadmin.com/subscribe');
              } else {
                navigation.navigate('MyActivityFeed');
              }
            }}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="chatbubbles-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Activity</Text>
              </View>
              {authChecked && !isAuthed && (
                <Ionicons
                  name="lock-closed-outline"
                  size={16}
                  color="#FDF05D"
                  style={styles.lockIcon}
                />
              )}
            </View>
          </TouchableOpacity>

          {/* Account → BusinessAccountPage */}
          <TouchableOpacity
            style={styles.navItem}
            onPress={() => navigation.navigate('BusinessAccountPage')}
          >
            <View style={styles.navItemRow}>
              <View style={styles.navItemMainLabel}>
                <Ionicons
                  name="person-circle-outline"
                  size={20}
                  color="#f5f5f5"
                  style={styles.navIcon}
                />
                <Text style={styles.navText}>Account</Text>
              </View>
            </View>
          </TouchableOpacity>
        </View>

        {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
        <View style={styles.sideNavSection}>
          {/* Peer Map */}
          <TouchableOpacity
            style={styles.peerMapCard}
            activeOpacity={0.9}
            onPress={() => openInSameTab('/venues')}
          >
            <LinearGradient
              colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
              style={styles.peerMapInner}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            >
              <View style={styles.peerMapContent}>
                <Ionicons
                  name="map-outline"
                  size={18}
                  color="#FDF05D"
                  style={{ marginRight: 8 }}
                />
                <View style={{ flex: 1 }}>
                  <Text style={styles.peerMapTitle}>Peer Map</Text>
                  <Text style={styles.peerMapSubtitle}>
                    View 100+ venues on Collabbay
                  </Text>
                </View>
              </View>
            </LinearGradient>
          </TouchableOpacity>

          {/* Subscribe CTA — only if NOT subscriber */}
          {!isSubscriber && (
            <TouchableOpacity
              style={styles.subscribeCard}
              activeOpacity={0.9}
              onPress={() => openInSameTab('https://collabbayadmin.com/subscribe')}
            >
              <LinearGradient
                colors={['#FDF05D', '#fbbf24']}
                style={styles.subscribeGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              >
                <View style={styles.subscribeContent}>
                  <Ionicons
                    name="rocket-outline"
                    size={20}
                    color="#000"
                    style={{ marginRight: 8 }}
                  />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.subscribeTitle}>Subscribe & unlock</Text>
                    <Text style={styles.subscribeSubtitle}>
                      Unlock Activity & Calendar, plus more.
                    </Text>
                  </View>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          )}
        </View>

        {/* BOTTOM: FAQ + Help */}
        <View style={styles.sideNavBottom}>
          <TouchableOpacity
            style={styles.bottomLink}
            onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}
          >
            <Ionicons
              name="help-circle-outline"
              size={18}
              color="#f5f5f5"
              style={{ marginRight: 8 }}
            />
            <View>
              <Text style={styles.bottomLinkText}>FAQs</Text>
              <Text style={styles.bottomLinkCaption}>
                Common questions answered
              </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.bottomLink}
            onPress={() => Linking.openURL('mailto:info@collab-bay.com')}
          >
            <Ionicons
              name="headset-outline"
              size={18}
              color="#f5f5f5"
              style={{ marginRight: 8 }}
            />
            <View>
              <Text style={styles.bottomLinkText}>Help</Text>
              <Text style={styles.bottomLinkCaption}>We&apos;re here 24/7</Text>
            </View>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );
}

function MobileTopBar({ navigation, title, openInSameTab }) {
  return (
    <View style={styles.mobileTopBar}>
      <TouchableOpacity
        style={styles.mobileBackButton}
        onPress={() => navigation.goBack()}
        activeOpacity={0.85}
      >
        <Ionicons name="arrow-back" size={20} color="#000" />
      </TouchableOpacity>

      <Text numberOfLines={1} style={styles.mobileTopTitle}>
        {title}
      </Text>

      <TouchableOpacity
        style={styles.mobileHomeButton}
        onPress={() => openInSameTab('https://collabbayadmin.com')}
        activeOpacity={0.85}
      >
        <Ionicons name="home-outline" size={20} color="#000" />
      </TouchableOpacity>
    </View>
  );
}

function ReviewCard({ review, index, isMobile, onDownload, onShare }) {
  const rating = Number(review.Rating || 0);
  const comment = String(review.Comment || '').trim();
  const promoter = review.Promoter || review.Creator || 'Creator';
  const dateText = formatDate(review.CreatedAt || review.DateCreated);
  const profileImage = String(review.ProfileImage || '').trim();
  const initial = promoter ? promoter.charAt(0).toUpperCase() : 'C';

  return (
    <View style={styles.reviewCard}>
      <LinearGradient
        colors={['rgba(253,240,93,0.11)', 'rgba(255,255,255,0)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.cardGlow}
        pointerEvents="none"
      />

      <View style={styles.reviewTopRow}>
        {profileImage ? (
          <Image
            source={{ uri: profileImage }}
            style={[styles.creatorAvatar, isMobile && styles.creatorAvatarMobile]}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.creatorAvatarFallback, isMobile && styles.creatorAvatarMobile]}>
            <Text style={styles.creatorAvatarInitial}>{initial}</Text>
          </View>
        )}

        <View style={styles.reviewCreatorBlock}>
          <Text style={styles.creatorName} numberOfLines={1}>
            {promoter}
          </Text>

          <View style={styles.verifiedRow}>
            <View style={styles.verifiedTick}>
              <Text style={styles.verifiedTickText}>✓</Text>
            </View>
            <Text style={styles.verifiedText}>Verified Review</Text>
          </View>

          <Text style={styles.reviewDate} numberOfLines={1}>
            {dateText}
          </Text>
        </View>
      </View>

      <Text style={[styles.largeStars, isMobile && styles.largeStarsMobile]} numberOfLines={1}>
        {ratingStars(rating)}
      </Text>

      <Text style={[styles.reviewComment, isMobile && styles.reviewCommentMobile]}>
        “{comment || 'No written comment left.'}”
      </Text>

      <View style={styles.reviewBottomRow}>
        <Text style={styles.visitRef} numberOfLines={1}>
          {review.CollabID ? `Visit ${review.CollabID}` : `Review #${index + 1}`}
        </Text>

        <Text style={styles.ratingText}>{rating}/5</Text>
      </View>

      <View style={[styles.reviewActionRow, isMobile && styles.reviewActionRowMobile]}>
        <TouchableOpacity
          style={[styles.downloadCta, isMobile && styles.reviewCtaMobile]}
          onPress={onDownload}
          activeOpacity={0.9}
        >
          <LinearGradient
            colors={['#fffdf0', '#FDF05D']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.downloadCtaGradient}
          >
            <Ionicons
              name="download-outline"
              size={18}
              color="#111"
              style={styles.reviewCtaIcon}
            />
            <Text style={styles.downloadCtaText}>Download PDF</Text>
          </LinearGradient>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.shareCta, isMobile && styles.reviewCtaMobile]}
          onPress={onShare}
          activeOpacity={0.9}
        >
          <Ionicons
            name="share-social-outline"
            size={18}
            color="#111"
            style={styles.reviewCtaIcon}
          />
          <Text style={styles.shareCtaText}>Share</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function ShareReviewModal({
  visible,
  review,
  brandName,
  onClose,
  onDownload,
  onShareSheet,
  onCopyText,
  onOpenSocial,
}) {
  if (!review) return null;

  const rating = Number(review.Rating || 0);
  const promoter = review.Promoter || review.Creator || 'Creator';
  const comment = String(review.Comment || '').trim();
  const profileImage = String(review.ProfileImage || '').trim();
  const initial = promoter ? promoter.charAt(0).toUpperCase() : 'C';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <TouchableOpacity
          style={styles.modalBackdrop}
          activeOpacity={1}
          onPress={onClose}
        />

        <View style={styles.shareModalCard}>
          <View style={styles.shareModalHandle} />

          <View style={styles.shareModalHeader}>
            {profileImage ? (
              <Image
                source={{ uri: profileImage }}
                style={styles.shareModalAvatar}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.shareModalAvatarFallback}>
                <Text style={styles.shareModalAvatarInitial}>{initial}</Text>
              </View>
            )}

            <View style={{ flex: 1 }}>
              <Text style={styles.shareModalTitle}>Share review asset</Text>
              <Text style={styles.shareModalSubtitle} numberOfLines={1}>
                {promoter} • {ratingStars(rating)}
              </Text>
            </View>

            <TouchableOpacity style={styles.modalCloseButton} onPress={onClose}>
              <Ionicons name="close" size={20} color="#000" />
            </TouchableOpacity>
          </View>

          <Text style={styles.shareModalQuote} numberOfLines={4}>
            “{comment || 'No written comment left.'}”
          </Text>

          <View style={styles.modalPrimaryRow}>
            <TouchableOpacity
              style={styles.modalDownloadButton}
              onPress={onDownload}
              activeOpacity={0.9}
            >
              <LinearGradient
                colors={['#fffdf0', '#FDF05D']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modalDownloadGradient}
              >
                <Ionicons
                  name="download-outline"
                  size={18}
                  color="#111"
                  style={styles.reviewCtaIcon}
                />
                <Text style={styles.modalDownloadButtonText}>Download PDF</Text>
              </LinearGradient>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.modalShareSheetButton}
              onPress={onShareSheet}
              activeOpacity={0.9}
            >
              <Ionicons
                name="share-outline"
                size={18}
                color="#111"
                style={styles.reviewCtaIcon}
              />
              <Text style={styles.modalShareSheetButtonText}>Share sheet</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.shareModalSectionLabel}>Post to social</Text>

          <View style={styles.socialGrid}>
            <SocialButton
              icon="logo-instagram"
              label="Instagram"
              onPress={() => onOpenSocial('instagram')}
            />
            <SocialButton
              icon="logo-facebook"
              label="Facebook"
              onPress={() => onOpenSocial('facebook')}
            />
            <SocialButton
              icon="logo-linkedin"
              label="LinkedIn"
              onPress={() => onOpenSocial('linkedin')}
            />
            <SocialButton
              icon="logo-whatsapp"
              label="WhatsApp"
              onPress={() => onOpenSocial('whatsapp')}
            />
            <SocialButton
              icon="logo-twitter"
              label="X"
              onPress={() => onOpenSocial('x')}
            />
            <SocialButton
              icon="copy-outline"
              label="Copy text"
              onPress={onCopyText}
            />
          </View>

          <Text style={styles.shareModalSmallPrint}>
            Tip: download the PDF first, then upload it as a post/story asset. Instagram opens with the caption copied where supported.
          </Text>
        </View>
      </View>
    </Modal>
  );
}

function SocialButton({ icon, label, onPress }) {
  return (
    <TouchableOpacity
      style={styles.socialButton}
      onPress={onPress}
      activeOpacity={0.88}
    >
      <View style={styles.socialIconCircle}>
        <Ionicons name={icon} size={18} color="#000" />
      </View>
      <Text style={styles.socialButtonText}>{label}</Text>
    </TouchableOpacity>
  );
}

function Chip({ label, active, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.chip, active && styles.activeChip]}
      activeOpacity={0.85}
    >
      <Text style={[styles.chipText, active && styles.activeChipText]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

function EmptyState({ hasReviews, filter, navigation }) {
  const message = hasReviews
    ? `No reviews match the "${filter}" filter.`
    : 'No positive creator reviews have been stored yet. 4-star and 5-star reviews will appear here after creator visits.';

  return (
    <View style={styles.emptyState}>
      <Text style={styles.emptyTitle}>
        {hasReviews ? 'Nothing to show here' : 'No review assets yet'}
      </Text>
      <Text style={styles.emptyMsg}>{message}</Text>

      <TouchableOpacity
        style={styles.ctaBtn}
        onPress={() => navigation.navigate('MyOffers')}
        activeOpacity={0.9}
      >
        <LinearGradient
          colors={['#fffdf0', '#FDF05D']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.ctaGradient}
        >
          <Text style={styles.ctaText}>View my offers</Text>
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );
}

function ratingStars(rating) {
  const safeRating = Math.max(0, Math.min(5, Number(rating || 0)));
  return `${'⭐'.repeat(safeRating)}${'☆'.repeat(5 - safeRating)}`;
}

function buildShareText(review, brandName) {
  const rating = Number(review.Rating || 0);
  const safeRating = Math.max(0, Math.min(5, rating));
  const comment = String(review.Comment || 'No written comment left.').trim();
  const promoter = review.Promoter || review.Creator || 'Creator';
  const brand = brandName || review.BrandName || review.Brand || 'this venue';

  return `“${comment}”

${'⭐'.repeat(safeRating)} ${safeRating}/5 verified creator review from ${promoter} for ${brand}.

Generated via Collabbay.`;
}

function formatDate(value) {
  if (!value) return 'Date unavailable';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function buildSingleReviewHtml(review, brandName) {
  const rating = Number(review.Rating || 0);
  const safeRating = Math.max(0, Math.min(5, rating));
  const comment = escapeHtml(review.Comment || 'No written comment left.');
  const promoter = escapeHtml(review.Promoter || review.Creator || 'Creator');
  const brand = escapeHtml(brandName || review.BrandName || review.Brand || 'Collabbay venue');
  const date = escapeHtml(formatDate(review.CreatedAt || review.DateCreated));
  const collabID = escapeHtml(review.CollabID || '');
  const profileImage = escapeHtml(review.ProfileImage || '');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${brand} Creator Review</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      background: #f6f6f4;
      color: #000;
      padding: 28px;
    }
    .page {
      max-width: 760px;
      margin: 0 auto;
      background: #fff;
      border-radius: 28px;
      padding: 34px;
      border: 1px solid #ececec;
      box-shadow: 0 18px 50px rgba(0,0,0,0.08);
    }
    .top {
      display: flex;
      align-items: center;
      gap: 18px;
      margin-bottom: 28px;
    }
    .avatar {
      width: 92px;
      height: 92px;
      border-radius: 50%;
      object-fit: cover;
      background: #eee;
    }
    .kicker {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #777;
      margin-bottom: 5px;
      font-weight: 700;
    }
    h1 {
      font-size: 32px;
      margin: 0;
      line-height: 1.1;
    }
    .verified {
      margin-top: 9px;
      display: inline-block;
      font-size: 12px;
      font-weight: 700;
      background: #f7f7f7;
      border: 1px solid #eeeeee;
      border-radius: 999px;
      padding: 6px 10px;
    }
    .stars {
      font-size: 40px;
      margin-bottom: 22px;
      letter-spacing: 1px;
    }
    .comment {
      font-size: 34px;
      line-height: 1.25;
      font-weight: 300;
      font-style: italic;
      margin-bottom: 30px;
      letter-spacing: -0.55px;
    }
    .meta {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      color: #333;
      font-size: 14px;
      border-top: 1px solid #eee;
      padding-top: 18px;
    }
    .footer {
      margin-top: 22px;
      font-size: 11px;
      color: #777;
    }
    @media print {
      body {
        background: #fff;
        padding: 0;
      }
      .page {
        border: none;
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="top">
      ${profileImage ? `<img class="avatar" src="${profileImage}" />` : ''}
      <div>
        <div class="kicker">Collabbay creator review</div>
        <h1>${brand}</h1>
        <div class="verified">✓ Verified Review</div>
      </div>
    </div>

    <div class="stars">${'⭐'.repeat(safeRating)}${'☆'.repeat(5 - safeRating)}</div>
    <div class="comment">“${comment}”</div>

    <div class="meta">
      <div><strong>Creator:</strong> ${promoter}</div>
      <div><strong>Date:</strong> ${date}</div>
    </div>

    ${collabID ? `<div class="footer">Visit reference: ${collabID}</div>` : ''}
    <div class="footer">Generated from Collabbay creator reviews.</div>
  </div>
</body>
</html>
`;
}

function buildAllReviewsHtml(reviews, brandName) {
  const brand = escapeHtml(brandName || 'Collabbay venue');

  const reviewBlocks = reviews
    .filter((review) => Number(review.Rating) >= POSITIVE_RATING_MIN)
    .map((review) => {
      const rating = Math.max(0, Math.min(5, Number(review.Rating || 0)));
      const comment = escapeHtml(review.Comment || 'No written comment left.');
      const promoter = escapeHtml(review.Promoter || review.Creator || 'Creator');
      const date = escapeHtml(formatDate(review.CreatedAt || review.DateCreated));
      const profileImage = escapeHtml(review.ProfileImage || '');

      return `
      <div class="review">
        <div class="reviewTop">
          ${profileImage ? `<img class="avatar" src="${profileImage}" />` : `<div class="avatar fallback">${promoter.charAt(0).toUpperCase()}</div>`}
          <div>
            <div class="creator">${promoter}</div>
            <div class="verified">✓ Verified Review</div>
            <div class="date">${date}</div>
          </div>
        </div>
        <div class="stars">${'⭐'.repeat(rating)}${'☆'.repeat(5 - rating)}</div>
        <div class="comment">“${comment}”</div>
      </div>
    `;
    })
    .join('');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${brand} Creator Reviews</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      background: #f6f6f4;
      color: #000;
      padding: 28px;
    }
    .page {
      max-width: 840px;
      margin: 0 auto;
      background: #fff;
      border-radius: 28px;
      padding: 34px;
      border: 1px solid #ececec;
      box-shadow: 0 18px 50px rgba(0,0,0,0.08);
    }
    .kicker {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #777;
      margin-bottom: 6px;
      font-weight: 700;
    }
    h1 {
      font-size: 34px;
      margin: 0 0 8px;
      line-height: 1.1;
    }
    .sub {
      color: #555;
      font-size: 15px;
      margin-bottom: 26px;
    }
    .review {
      border-top: 1px solid #eee;
      padding: 24px 0;
      page-break-inside: avoid;
    }
    .reviewTop {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 18px;
    }
    .avatar {
      width: 76px;
      height: 76px;
      border-radius: 50%;
      object-fit: cover;
      background: #eee;
    }
    .fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      background: #FDF05D;
    }
    .creator {
      font-size: 18px;
      font-weight: 800;
    }
    .verified {
      display: inline-block;
      font-size: 12px;
      font-weight: 700;
      background: #f7f7f7;
      border: 1px solid #eeeeee;
      border-radius: 999px;
      padding: 5px 9px;
      margin-top: 6px;
    }
    .date {
      color: #777;
      font-size: 13px;
      margin-top: 6px;
    }
    .stars {
      font-size: 34px;
      margin-bottom: 14px;
    }
    .comment {
      font-size: 28px;
      line-height: 1.32;
      font-weight: 300;
      font-style: italic;
      letter-spacing: -0.45px;
    }
    .footer {
      margin-top: 28px;
      font-size: 11px;
      color: #777;
    }
    @media print {
      body {
        background: #fff;
        padding: 0;
      }
      .page {
        border: none;
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="kicker">Collabbay creator reviews</div>
    <h1>${brand}</h1>
    <div class="sub">${reviews.length} reusable verified creator review asset${reviews.length === 1 ? '' : 's'}</div>
    ${reviewBlocks}
    <div class="footer">Generated from Collabbay creator reviews.</div>
  </div>
</body>
</html>
`;
}

async function openPrintableHtml(html, title) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const printWindow = window.open('', '_blank', 'width=900,height=700');

    if (!printWindow) {
      alert('Please allow popups to download or print this review.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.document.title = title;
      printWindow.focus();
      printWindow.print();
    };

    setTimeout(() => {
      try {
        printWindow.document.title = title;
        printWindow.focus();
        printWindow.print();
      } catch (error) {
        console.error('Print failed:', error);
      }
    }, 500);

    return;
  }

  const plainText = html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  try {
    await Share.share({
      title,
      message: plainText,
    });
  } catch (error) {
    console.error('Share failed:', error);
  }
}

const styles = StyleSheet.create({
  /* APP SHELL */
  safeArea: {
    flex: 1,
    backgroundColor: '#f7f7f3',
  },
  appShell: {
    flex: 1,
    backgroundColor: '#f7f7f3',
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  /* SIDE NAV */
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  mainArea: {
    flex: 1,
    backgroundColor: '#f7f7f3',
  },
  scrollContainer: {
    paddingHorizontal: 26,
    paddingVertical: 24,
  },
  scrollContainerMobile: {
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 28,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
  },

  loadingContainer: {
    flex: 1,
    backgroundColor: '#f7f7f3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    fontSize: 13,
    color: '#6b7280',
  },

  mobileTopBar: {
    height: 58,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#ededed',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mobileBackButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#f3f3f3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileHomeButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#f3f3f3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileTopTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '800',
    color: '#000',
    marginHorizontal: 10,
  },

  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#e9e9e9',
    shadowColor: '#000',
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '600',
  },
  closeButton: {
    backgroundColor: '#fff',
    padding: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#e9e9e9',
    shadowColor: '#000',
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },

  cleanHeader: {
    marginBottom: 18,
  },
  assetExplainer: {
    backgroundColor: '#fff',
    borderRadius: 22,
    paddingVertical: 14,
    paddingHorizontal: 15,
    borderWidth: 1,
    borderColor: '#ece9dc',
    marginBottom: 18,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.035,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
  },
  assetDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FDF05D',
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#e7d930',
  },
  assetExplainerText: {
    flex: 1,
    fontSize: 13,
    color: '#333',
    lineHeight: 19,
    fontWeight: '600',
  },
  kicker: {
    fontSize: 11,
    color: '#737373',
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    fontWeight: '800',
    marginBottom: 6,
  },
  header: {
    fontSize: 34,
    fontWeight: '900',
    color: '#000',
    letterSpacing: -0.7,
    marginBottom: 6,
  },
  subhead: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    lineHeight: 21,
  },
  headerActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
  },
  smallAction: {
    backgroundColor: '#fff',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#e7e7e7',
    marginRight: 8,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.025,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
  },
  smallActionIcon: {
    marginRight: 6,
  },
  smallActionText: {
    fontSize: 13,
    fontWeight: '800',
    color: '#000',
  },
  primaryAction: {
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#eadf59',
    shadowColor: '#000',
    shadowOpacity: 0.055,
    shadowRadius: 9,
    shadowOffset: { width: 0, height: 4 },
  },
  primaryActionGradient: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 999,
    flexDirection: 'row',
    alignItems: 'center',
  },
  primaryActionIcon: {
    marginRight: 7,
  },
  primaryActionText: {
    fontSize: 13,
    color: '#111',
    fontWeight: '900',
  },
  disabledAction: {
    opacity: 0.45,
  },

  controlsWrap: {
    marginBottom: 14,
  },
  chipsScroll: {
    paddingRight: 12,
  },
  chip: {
    paddingVertical: 9,
    paddingHorizontal: 14,
    backgroundColor: '#fff',
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e6e4db',
    shadowColor: '#000',
    shadowOpacity: 0.018,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  },
  activeChip: {
    backgroundColor: '#fffbe3',
    borderColor: '#e7d930',
  },
  chipText: {
    fontSize: 13,
    fontWeight: '750',
    color: '#555',
  },
  activeChipText: {
    color: '#000',
    fontWeight: '900',
  },

  reviewList: {
    width: '100%',
  },
  reviewCard: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 32,
    padding: 22,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#ece9df',
    position: 'relative',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.055,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 2,
  },
  cardGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 120,
  },
  reviewTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 18,
    zIndex: 2,
  },
  creatorAvatar: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: '#eee',
    marginRight: 16,
    borderWidth: 3,
    borderColor: '#fff',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
  },
  creatorAvatarMobile: {
    width: 74,
    height: 74,
    borderRadius: 37,
    marginRight: 13,
  },
  creatorAvatarFallback: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    borderWidth: 3,
    borderColor: '#fff',
  },
  creatorAvatarInitial: {
    fontSize: 27,
    color: '#000',
    fontWeight: '900',
  },
  reviewCreatorBlock: {
    flex: 1,
    paddingRight: 10,
  },
  creatorName: {
    fontSize: 18,
    color: '#000',
    fontWeight: '900',
    letterSpacing: -0.2,
  },
  verifiedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 7,
  },
  verifiedTick: {
    width: 17,
    height: 17,
    borderRadius: 8.5,
    backgroundColor: '#111',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 7,
  },
  verifiedTickText: {
    color: '#FDF05D',
    fontSize: 10,
    fontWeight: '900',
    lineHeight: 13,
  },
  verifiedText: {
    fontSize: 12,
    color: '#222',
    fontWeight: '800',
  },
  reviewDate: {
    fontSize: 12,
    color: '#777',
    marginTop: 5,
    fontWeight: '600',
  },
  largeStars: {
    fontSize: 40,
    lineHeight: 48,
    marginBottom: 14,
    letterSpacing: 1,
  },
  largeStarsMobile: {
    fontSize: 31,
    lineHeight: 40,
  },
  reviewComment: {
    fontSize: 30,
    color: '#111',
    fontWeight: '300',
    fontStyle: 'italic',
    lineHeight: 39,
    letterSpacing: -0.55,
  },
  reviewCommentMobile: {
    fontSize: 24,
    lineHeight: 33,
  },
  reviewBottomRow: {
    marginTop: 20,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#f0eee7',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  visitRef: {
    flex: 1,
    fontSize: 11,
    color: '#888',
    fontWeight: '600',
    marginRight: 10,
  },
  ratingText: {
    fontSize: 12,
    color: '#000',
    fontWeight: '900',
  },
  reviewActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
  },
  reviewActionRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  downloadCta: {
    flex: 1,
    minHeight: 50,
    borderRadius: 999,
    marginRight: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e7d930',
    shadowColor: '#000',
    shadowOpacity: 0.045,
    shadowRadius: 9,
    shadowOffset: { width: 0, height: 4 },
  },
  downloadCtaGradient: {
    flex: 1,
    borderRadius: 999,
    paddingVertical: 14,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareCta: {
    minHeight: 50,
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingVertical: 14,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#dfded7',
    shadowColor: '#000',
    shadowOpacity: 0.025,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
  reviewCtaMobile: {
    width: '100%',
    marginRight: 0,
    marginBottom: 10,
  },
  reviewCtaIcon: {
    marginRight: 8,
  },
  downloadCtaText: {
    color: '#111',
    fontSize: 15,
    fontWeight: '900',
  },
  shareCtaText: {
    color: '#111',
    fontSize: 15,
    fontWeight: '900',
  },

  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.38)',
  },
  shareModalCard: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    paddingTop: 10,
    paddingHorizontal: 18,
    paddingBottom: 24,
    borderWidth: 1,
    borderColor: '#ededed',
    shadowColor: '#000',
    shadowOpacity: 0.14,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: -6 },
  },
  shareModalHandle: {
    width: 46,
    height: 5,
    borderRadius: 999,
    backgroundColor: '#ddd',
    alignSelf: 'center',
    marginBottom: 18,
  },
  shareModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  shareModalAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#eee',
    marginRight: 12,
  },
  shareModalAvatarFallback: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  shareModalAvatarInitial: {
    fontSize: 21,
    fontWeight: '900',
    color: '#000',
  },
  shareModalTitle: {
    fontSize: 19,
    color: '#000',
    fontWeight: '900',
    letterSpacing: -0.3,
  },
  shareModalSubtitle: {
    fontSize: 12,
    color: '#666',
    marginTop: 3,
    fontWeight: '600',
  },
  modalCloseButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#f5f5f2',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10,
    borderWidth: 1,
    borderColor: '#ececec',
  },
  shareModalQuote: {
    fontSize: 20,
    lineHeight: 28,
    color: '#111',
    fontWeight: '300',
    fontStyle: 'italic',
    marginBottom: 18,
    letterSpacing: -0.25,
  },
  modalPrimaryRow: {
    flexDirection: 'row',
    marginBottom: 18,
  },
  modalDownloadButton: {
    flex: 1,
    borderRadius: 999,
    minHeight: 50,
    marginRight: 9,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e7d930',
  },
  modalDownloadGradient: {
    flex: 1,
    borderRadius: 999,
    paddingVertical: 13,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalDownloadButtonText: {
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
  },
  modalShareSheetButton: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 999,
    minHeight: 50,
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: '#dfded7',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalShareSheetButtonText: {
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
  },
  shareModalSectionLabel: {
    fontSize: 12,
    color: '#777',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 10,
  },
  socialGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  socialButton: {
    width: '48.5%',
    backgroundColor: '#fbfbf8',
    borderRadius: 19,
    paddingVertical: 13,
    paddingHorizontal: 12,
    marginBottom: 9,
    borderWidth: 1,
    borderColor: '#eeeeee',
    flexDirection: 'row',
    alignItems: 'center',
  },
  socialIconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#eeeeee',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 9,
  },
  socialButtonText: {
    fontSize: 13,
    color: '#000',
    fontWeight: '800',
  },
  shareModalSmallPrint: {
    fontSize: 11,
    color: '#777',
    lineHeight: 16,
    marginTop: 4,
  },

  errorBox: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#f0c7ce',
  },
  errorText: {
    fontSize: 13,
    color: '#b00020',
    fontWeight: '700',
  },

  emptyState: {
    backgroundColor: '#fff',
    borderRadius: 28,
    padding: 30,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ededed',
    marginTop: 10,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
  },
  emptyTitle: {
    fontSize: 21,
    fontWeight: '900',
    color: '#000',
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  emptyMsg: {
    fontSize: 14,
    color: '#555',
    textAlign: 'center',
    lineHeight: 21,
    maxWidth: 480,
  },
  ctaBtn: {
    borderRadius: 999,
    marginTop: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e7d930',
  },
  ctaGradient: {
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 999,
  },
  ctaText: {
    fontWeight: '900',
    color: '#111',
    fontSize: 14,
  },
});